import MainLayout from "@/layouts/MainLayout.vue"
import HomeDashboard from "@/views/HomeDashboard.vue"
import BeepaidGoDashboard from "@/views/BeepaidGoDashboard.vue"
import ControlLogin from "@/views/ControlLogin.vue"
import HandleLoginLink from "@/views/HandleLoginLink.vue"
import MerchantIndex from "@/views/MerchantIndex.vue"
import MerchantDetail from "@/views/MerchantDetail.vue"
import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router"
import type { Component } from "vue"
import MerchantGroupIndex from "@/views/MerchantGroupIndex.vue"
import MerchantGroupEdit from "@/views/MerchantGroupEdit.vue"
import MerchantGroupCreate from "@/views/MerchantGroupCreate.vue"
import BulkUnpairTerminals from "@/views/BulkUnpairTerminals.vue"
import MerchantCreate from "@/views/MerchantCreate.vue"
import MerchantEdit from "@/views/MerchantEdit.vue"
import EngagementOverview from "@/views/EngagementOverview.vue"
import TerminalIndex from "@/views/TerminalIndex.vue"
import UserIndex from "@/views/UserIndex.vue"
import UserCreate from "@/views/UserCreate.vue"
import UserEdit from "@/views/UserEdit.vue"

import RoleIndex from "@/views/RoleIndex.vue"
import RoleCreate from "@/views/RoleCreate.vue"
import RoleEdit from "@/views/RoleEdit.vue"

import MerchantGroupDetail from "@/views/MerchantGroupDetail.vue"
import type { RouteNamedMap } from "./RouteNamedMap"
import NotificationsIndex from "@/views/NotificationsIndex.vue"
import NotificationCreate from "@/views/NotificationCreate.vue"
import BeepaidGoDashboardIcon from "@/components/BeepaidGoDashboardIcon.vue"
import {
  IconMerchant,
  IconMerchantGroup,
  IconTerminal,
  IconDashboard,
  IconBulkUnpairTerminals,
  IconEngagementOverview,
  IconUsers,
  IconNotifications,
  IconRole,
} from "@/icons"
import PasswordResetRequest from "@/views/PasswordResetRequest.vue"
import SetChangePassword from "@/views/SetChangePassword.vue"
import ResetPassword from "@/views/ResetPassword.vue"

declare module "vue-router" {
  interface RouteMeta {
    pageTitle?: string
    icon?: Component
  }
  interface TypesConfig {
    RouteNamedMap: RouteNamedMap
  }
}

const { setPageTitle } = usePageTitle()

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: MainLayout,
    children: [
      {
        path: "",
        name: "dashboard",
        component: HomeDashboard,
        meta: {
          pageTitle: "TP+ Dashboard",
          icon: IconDashboard,
        },
      },

      {
        path: "beepaidGo",
        name: "beepaidGoDashboard",
        component: BeepaidGoDashboard,
        meta: {
          pageTitle: "Beepaid Go Dashboard",
          icon: BeepaidGoDashboardIcon,
        },
      },
      {
        path: "engagement",
        name: "engagementOverview",
        component: EngagementOverview,
        meta: {
          pageTitle: "Engagement Overview",
          icon: IconEngagementOverview,
        },
      },
      {
        path: "merchants",
        children: [
          {
            path: "",
            name: "merchantIndex",
            component: MerchantIndex,
            meta: {
              pageTitle: "Merchants",
              icon: IconMerchant,
            },
            children: [
              {
                path: ":tenantId",
                name: "merchantDetail",
                component: MerchantDetail,
                meta: {
                  pageTitle: "Merchants",
                },
                props: true,
              },
            ],
          },
          {
            path: "create",
            name: "merchantCreate",
            component: MerchantCreate,
            meta: {
              pageTitle: "Create Merchant",
              icon: IconMerchant,
            },
          },
          {
            path: ":merchantId/edit",
            name: "merchantEdit",
            component: MerchantEdit,
            props: true,
            meta: {
              pageTitle: "Edit Merchant",
            },
          },
          {
            path: "merchant-groups",
            name: "merchantGroupIndex",
            component: MerchantGroupIndex,
            meta: {
              pageTitle: "Merchant Groups",
              icon: IconMerchantGroup,
            },
            children: [
              {
                path: ":merchantGroupId",
                name: "merchantGroupDetail",
                component: MerchantGroupDetail,
                props: true,
                meta: {
                  pageTitle: "Merchant Groups",
                },
              },
            ],
          },
          {
            path: "merchant-groups/:merchantGroupId/edit",
            name: "merchantGroupEdit",
            component: MerchantGroupEdit,
            props: true,
            meta: {
              pageTitle: "Edit Merchant Group",
            },
          },
          {
            path: "merchant-groups/create",
            name: "merchantGroupCreate",
            component: MerchantGroupCreate,
            meta: {
              pageTitle: "Create Merchant Group",
            },
          },
        ],
      },
      {
        path: "terminals",
        children: [
          {
            path: "",
            name: "terminalIndex",
            component: TerminalIndex,
            meta: {
              pageTitle: "Terminals",
              icon: IconTerminal,
            },
          },
          {
            path: "bulk-unpair",
            name: "bulkUnpairTerminals",
            component: BulkUnpairTerminals,
            meta: {
              pageTitle: "Bulk Unpair Terminals",
              icon: IconBulkUnpairTerminals,
            },
          },
        ],
      },
      {
        path: "users",
        children: [
          {
            path: "",
            name: "userIndex",
            component: UserIndex,
            meta: {
              pageTitle: "Users",
              icon: IconUsers,
            },
          },
          {
            path: "create",
            name: "userCreate",
            component: UserCreate,
            meta: {
              pageTitle: "Create User",
            },
          },
          {
            path: ":userId/edit",
            name: "userEdit",
            component: UserEdit,
            props: true,
            meta: {
              pageTitle: "Edit User",
            },
          },
        ],
      },
      {
        path: "roles",
        children: [
          {
            path: "",
            name: "roleIndex",
            component: RoleIndex,
            meta: {
              pageTitle: "Roles",
              icon: IconRole,
            },
          },
          {
            path: "create",
            name: "roleCreate",
            component: RoleCreate,
            meta: {
              pageTitle: "Create Role",
            },
          },
          {
            path: ":roleId/edit",
            name: "roleEdit",
            component: RoleEdit,
            props: true,
            meta: {
              pageTitle: "Edit Role",
            },
          },
        ],
      },
      {
        path: "notifications",
        children: [
          {
            path: "",
            name: "notificationIndex",
            component: NotificationsIndex,
            meta: {
              pageTitle: "Notifications",
              icon: IconNotifications,
            },
          },
          {
            path: "create",
            name: "notificationCreate",
            component: NotificationCreate,
            meta: {
              pageTitle: "Create Notification",
            },
          },
        ],
      },
      {
        path: "/settings/password",
        name: "userPasswordChange",
        component: SetChangePassword,
      },
    ],
  },
  {
    path: "/login",
    name: "login",
    component: ControlLogin,
    meta: {
      pageTitle: "Log In",
    },
  },
  {
    path: "/handle-login",
    name: "handleLogin",
    component: HandleLoginLink,
  },
  {
    path: "/password-reset-request",
    name: "passwordResetRequest",
    component: PasswordResetRequest,
  },
  {
    path: "/:pathMatch(.*)",
    redirect: {
      name: "login",
    },
  },
  {
    path: "/reset-password",
    name: "resetPassword",
    component: ResetPassword,
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach((to) => {
  setPageTitle(to.meta.pageTitle || "")
  return true
})

export default router
